import { useState } from 'react';
import type { AltaTipoEntrada, TipoEntrada } from '../../api/ops/eventos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { CampoImporte } from '../../componentes/CampoImporte';
import { Casilla } from '../../componentes/Casilla';
import { Chip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { aCentimos, dinero, textoDeCentimos } from '../../logica/dinero';
import { avisoBreveDeError } from '../../logica/estados';
import type { PropsPestana } from './Ficha';
import { erroresTipo, NOTA_GASTOS, precioTipo, type FormValores } from './formulario';
import { Ceja, CampoDiaHora } from './piezas';
import { aCampo, COLORES, deCampo, erroresDeCampo, ESTADO_TIPO, lineaTipo, nombreColor, textoErrorGuardar, vendidasTipo, versionDeTipo } from './reglas';

/**
 * Tipos de entrada (10.3): la lista de la pestaña Entradas con su precio y «paga X» (`price_with_fees`, G786; «Gratis»
 * sin gastos), lo vendido de su cupo, cuándo y dónde se vende y su estado; y la segunda tarjeta para crear o editar
 * (precio, cupo propio, desde/hasta, mínimo y máximo por pedido, online y taquilla, color en la taquilla), pausar o
 * reanudar y archivar con confirmación. El precio de lo ya vendido no cambia (lo garantiza la API).
 */
export function ListaTipos({ datos, edita, abrirTipo }: PropsPestana) {
    const e = datos.evento;
    const tipos = datos.tipos;
    const vivos = tipos.filter((t) => t.status !== 'archived');
    const activos = tipos.filter((t) => t.status === 'active').length;
    return (
        <section className="rv-ev-tipos" aria-label="Tipos de entrada">
            <div className="rv-ev-tipos__cab">
                <Ceja>Tipos de entrada</Ceja>
                {vivos.length > 0 && <span className="rv-ev-texto">{`${String(activos)} de ${String(vivos.length)} activos`}</span>}
            </div>
            {tipos.length === 0 ? (
                <div className="rv-ev-vacio">
                    <strong>Todavía no hay tipos de entrada</strong>
                    <span>General, anticipada, mesa… Cada uno con su precio, su cupo y sus fechas de venta. Hace falta al menos uno para publicar.</span>
                    {edita && (
                        <Boton tipo="secundario" icono="plus" onClick={() => { abrirTipo('nuevo'); }}>
                            Nuevo tipo de entrada
                        </Boton>
                    )}
                </div>
            ) : (
                <ul className="rv-ev-tipos__lista">
                    {tipos.map((t) => {
                        const p = precioTipo(t);
                        const tope = t.quota ?? e.capacity_detail.capacity;
                        const [estado, tono] = ESTADO_TIPO[t.status];
                        return (
                            <li key={t.id}>
                                <button type="button" className="rv-ev-tipo" onClick={() => { abrirTipo(t); }} aria-label={`Abrir el tipo ${t.name ?? ''}`}>
                                    {t.color != null && <span className="rv-ev-tipo__color" style={{ background: t.color }} aria-hidden="true" />}
                                    <span className="rv-ev-tipo__texto">
                                        <strong>
                                            {t.name}
                                            {t.status !== 'active' && <Chip estado={tono}>{estado}</Chip>}
                                        </strong>
                                        <small>{lineaTipo(t, e.timezone)}</small>
                                        <span className="rv-ev-tipo__barra">
                                            <span className="rv-ev-barra rv-ev-barra--fina" aria-hidden="true">
                                                <span style={{ width: `${String(Math.min(100, (t.confirmed_count / Math.max(1, tope)) * 100))}%`, background: 'var(--rv-ink)' }} />
                                                <span style={{ width: `${String(Math.min(100, (t.held_count / Math.max(1, tope)) * 100))}%`, background: 'var(--rv-warning)' }} />
                                            </span>
                                            <small>{vendidasTipo(t)}</small>
                                        </span>
                                    </span>
                                    <span className="rv-ev-tipo__precio">
                                        <strong>{p.precio}</strong>
                                        {p.paga !== '' && <small>{p.paga}</small>}
                                    </span>
                                    <Icono nombre="chevron-right" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            <p className="rv-ev-texto rv-ev-texto--nota">
                <Icono nombre="percent" />
                <span>{NOTA_GASTOS}</span>
            </p>
        </section>
    );
}

export function FormTipo({ api, negocio, movil, datos, edita, tipo, onCerrar }: PropsPestana & { tipo: TipoEntrada | null; onCerrar: (cambiado: boolean) => void }) {
    const e = datos.evento;
    const zona = e.timezone;
    const avisar = useAvisoBreve();
    const moneda = tipo?.price?.currency ?? datos.tipos.find((t) => t.price !== undefined)?.price?.currency ?? 'EUR';
    const inicial: FormValores = {
        nombre: tipo?.name ?? '',
        descripcion: tipo?.description ?? '',
        precio: tipo?.price === undefined ? '' : textoDeCentimos(tipo.price.amount, true),
        conCupo: tipo?.quota != null,
        cupo: tipo?.quota == null ? '' : String(tipo.quota),
        desde: aCampo(tipo?.sales_start_at, zona),
        hasta: aCampo(tipo?.sales_end_at, zona),
        minimo: String(tipo?.min_per_order ?? 1),
        maximo: String(tipo?.max_per_order ?? Math.min(10, e.max_tickets_per_order ?? 10)),
        online: tipo?.sold_online ?? true,
        taquilla: tipo?.sold_at_box_office ?? false,
        color: tipo?.color ?? null,
    };
    const [v, setV] = useState<FormValores>(inicial);
    const [conDescripcion, setConDescripcion] = useState(inicial.descripcion !== '');
    const [intentado, setIntentado] = useState(false);
    const [deApi, setDeApi] = useState<Record<string, string>>({});
    const [ocupado, setOcupado] = useState(false);
    const [archivar, setArchivar] = useState(false);
    const vendidas = (tipo?.confirmed_count ?? 0) + (tipo?.held_count ?? 0);
    const editaEste = edita && tipo?.status !== 'archived';
    const errores = { ...(intentado ? erroresTipo(v, vendidas) : {}), ...deApi };
    const cambia = <K extends keyof FormValores>(k: K) => (valor: FormValores[K]) => {
        setV((x) => ({ ...x, [k]: valor }));
        setDeApi({});
    };

    const cuerpo = (): AltaTipoEntrada => ({
        name: v.nombre.trim(),
        ...(v.descripcion.trim() === '' ? {} : { description: v.descripcion.trim() }),
        price: { amount: aCentimos(v.precio) ?? 0, currency: moneda },
        quota: v.conCupo ? Number(v.cupo) : null,
        sales_start_at: v.desde === '' ? null : deCampo(v.desde, zona),
        sales_end_at: v.hasta === '' ? null : deCampo(v.hasta, zona),
        min_per_order: Number(v.minimo),
        max_per_order: Number(v.maximo),
        sold_online: v.online,
        sold_at_box_office: v.taquilla,
        color: v.color,
    });

    const fallo = (err: unknown) => {
        setOcupado(false);
        const campos = erroresDeCampo(err);
        setDeApi(campos);
        if (Object.keys(campos).length === 0) {
            avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? (err instanceof ErrorApi && err.codigo === 'version_conflict' ? 'Otra persona ha cambiado este tipo. Ciérralo y vuelve a abrirlo.' : textoErrorGuardar(err)) });
        }
    };

    const guardar = () => {
        setIntentado(true);
        if (Object.keys(erroresTipo(v, vendidas)).length > 0) {
            return;
        }
        setOcupado(true);
        const peticion = tipo === null ? api.createTicketType(negocio, e.id, cuerpo()) : api.updateTicketType(negocio, e.id, tipo.id, cuerpo(), versionDeTipo(tipo));
        peticion
            .then(() => {
                avisar({ tipo: 'ok', texto: tipo === null ? 'Tipo de entrada creado.' : 'Cambios guardados.' });
                onCerrar(true);
            })
            .catch(fallo);
    };

    const pausar = () => {
        if (tipo === null) {
            return;
        }
        setOcupado(true);
        api.updateTicketType(negocio, e.id, tipo.id, { status: tipo.status === 'paused' ? 'active' : 'paused' }, versionDeTipo(tipo))
            .then(() => {
                avisar({ tipo: 'ok', texto: tipo.status === 'paused' ? 'Venta reanudada.' : 'Venta en pausa.' });
                onCerrar(true);
            })
            .catch(fallo);
    };

    const hacerArchivo = () => {
        if (tipo === null) {
            return;
        }
        setOcupado(true);
        api.archiveTicketType(negocio, e.id, tipo.id)
            .then(() => {
                setArchivar(false);
                avisar({ tipo: 'ok', texto: 'Tipo archivado.' });
                onCerrar(true);
            })
            .catch((err: unknown) => {
                setArchivar(false);
                fallo(err);
            });
    };

    const precioIgual = tipo?.price !== undefined && aCentimos(v.precio) === tipo.price.amount;
    const paga = aCentimos(v.precio) === 0 ? 'Gratis, sin gastos de gestión' : precioIgual && tipo.price_with_fees !== undefined ? dinero(tipo.price_with_fees) : '—';

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { onCerrar(false); }}
                movil={movil}
                titulo={tipo === null ? 'Nuevo tipo' : (tipo.name ?? '')}
                eyebrow={tipo === null ? 'Nuevo tipo de entrada' : 'Tipo de entrada'}
                subtitulo={e.name}
                icono="ticket"
                accion={editaEste ? (tipo === null ? 'Crear tipo' : 'Guardar') : ''}
                ocupado={ocupado}
                onAccion={guardar}
            >
                <div className="rv-ev-form">
                    <Campo etiqueta="Nombre" valor={v.nombre} onChange={cambia('nombre')} error={errores.name} ayuda="Por ejemplo: General, Anticipada, Mesa." bloqueado={!editaEste} max={70} />
                    {conDescripcion ? (
                        <Campo etiqueta="Descripción" valor={v.descripcion} onChange={cambia('descripcion')} error={errores.description} bloqueado={!editaEste} filas={2} max={500} />
                    ) : (
                        editaEste && (
                            <button type="button" className="rv-ev-enlace" onClick={() => { setConDescripcion(true); }}>
                                <Icono nombre="plus" />
                                <span>Añadir una descripción</span>
                            </button>
                        )
                    )}

                    <Ceja>Precio</Ceja>
                    <CampoImporte etiqueta="Precio de la entrada" valor={v.precio} onChange={cambia('precio')} moneda={moneda} error={errores.price} ayuda={vendidas > 0 ? 'El precio de lo ya vendido no cambia.' : '0 = gratis.'} bloqueado={!editaEste} />
                    <div className="rv-ev-paga">
                        <strong>El comprador paga</strong>
                        <span>{paga}</span>
                    </div>
                    {paga === '—' && <span className="rv-ev-texto">Lo calculamos al guardar, con los gastos de gestión vigentes.</span>}

                    <Ceja>Cupo</Ceja>
                    <Interruptor etiqueta="Cupo propio" valor={v.conCupo} onChange={cambia('conCupo')} bloqueado={!editaEste} ayuda={v.conCupo ? 'No más que el aforo ni menos de lo vendido.' : 'Sin cupo propio: manda el aforo del evento.'} />
                    {v.conCupo && <Campo etiqueta="Entradas de este tipo" tipo="number" modo="numeric" valor={v.cupo} onChange={cambia('cupo')} error={errores.quota} bloqueado={!editaEste} />}

                    <Ceja>Cuándo se vende</Ceja>
                    <CampoDiaHora etiqueta="Desde" valor={v.desde} onChange={cambia('desde')} error={errores.sales_start_at} ayuda={v.desde === '' ? 'Vacío: desde que publiques.' : null} bloqueado={!editaEste} />
                    <CampoDiaHora etiqueta="Hasta" valor={v.hasta} onChange={cambia('hasta')} error={errores.sales_end_at} ayuda={v.hasta === '' ? 'Vacío: hasta que empiece el evento.' : null} bloqueado={!editaEste} />
                    <div className="rv-ca-dos">
                        <Campo etiqueta="Mínimo por pedido" tipo="number" modo="numeric" valor={v.minimo} onChange={cambia('minimo')} error={errores.min_per_order} bloqueado={!editaEste} />
                        <Campo etiqueta="Máximo por pedido" tipo="number" modo="numeric" valor={v.maximo} onChange={cambia('maximo')} error={errores.max_per_order} bloqueado={!editaEste} />
                    </div>

                    <Ceja>Dónde se vende</Ceja>
                    <Casilla marcada={v.online} onCambio={cambia('online')} bloqueada={!editaEste}>
                        Online: web, widget y asistente
                    </Casilla>
                    <Casilla marcada={v.taquilla} onCambio={cambia('taquilla')} bloqueada={!editaEste}>
                        En taquilla
                    </Casilla>
                    {errores.sold_online !== undefined && <span className="rv-ev-error" role="alert">{errores.sold_online}</span>}

                    <Ceja>Color en la taquilla</Ceja>
                    <div className="rv-ev-colores" role="radiogroup" aria-label="Color en la taquilla">
                        {[{ nombre: 'Neutro', hex: null as string | null }, ...COLORES].map((c) => (
                            <button
                                key={c.nombre}
                                type="button"
                                role="radio"
                                aria-checked={(v.color ?? null) === c.hex}
                                aria-label={c.nombre}
                                title={c.nombre}
                                disabled={!editaEste}
                                className={`rv-ev-color${(v.color ?? null) === c.hex ? ' rv-ev-color--elegido' : ''}`}
                                style={{ background: c.hex ?? 'var(--rv-paper)' }}
                                onClick={() => { cambia('color')(c.hex); }}
                            >
                                {(v.color ?? null) === c.hex && <Icono nombre="check" />}
                            </button>
                        ))}
                    </div>
                    <span className="rv-ev-texto">{`${nombreColor(v.color)}: así se ve su baldosa en la taquilla.`}</span>

                    {tipo !== null && editaEste && (tipo.status === 'active' || tipo.status === 'paused') && (
                        <div className="rv-ev-bloque">
                            <span className="rv-ev-botones">
                                <Boton tipo="secundario" icono={tipo.status === 'paused' ? 'play' : 'pause'} onClick={pausar} disabled={ocupado}>
                                    {tipo.status === 'paused' ? 'Reanudar la venta' : 'Pausar la venta'}
                                </Boton>
                                <Boton tipo="secundario" rojo icono="archive" onClick={() => { setArchivar(true); }} disabled={ocupado}>
                                    Archivar
                                </Boton>
                            </span>
                            <span className="rv-ev-texto">
                                {tipo.confirmed_count > 0
                                    ? `Archivar lo quita de la venta por todos los canales. Sus ${String(tipo.confirmed_count)} entradas siguen valiendo.`
                                    : 'Archivar lo quita de la venta por todos los canales. No se puede deshacer.'}
                            </span>
                        </div>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={archivar}
                titulo={`¿Archivar «${tipo?.name ?? ''}»?`}
                texto="Deja de venderse por todos los canales. No se puede deshacer."
                accion="Archivar"
                accionIcono="archive"
                ocupado={ocupado}
                ocupadoTexto="Archivando…"
                movil={movil}
                onAccion={hacerArchivo}
                onCerrar={() => { setArchivar(false); }}
            />
        </>
    );
}
